import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtRelative, cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';

type UserDetail = {
  id: string;
  email: string;
  username: string | null;
  displayName: string;
  avatarUrl: string | null;
  preferredLanguage: string;
  role: 'user' | 'admin' | 'super_admin';
  telegramId: number | null;
  lastSeenAt: string | null;
  createdAt: string;
  travelerProfile: unknown;
  counts: {
    trips: number;
    feedPosts: number;
    aiConversations: number;
    esimPurchases: number;
  };
};

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const { data, isLoading } = useQuery<UserDetail>({
    queryKey: ['/admin/api/users', id],
    queryFn: () => api.get<UserDetail>(`/admin/api/users/${id}`),
    enabled: !!id,
  });

  const setRole = useMutation({
    mutationFn: (role: string) => api.patch(`/admin/api/users/${id}/role`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['/admin/api/users'] }),
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/admin/api/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['/admin/api/users'] });
      nav('/users');
    },
  });

  if (isLoading || !data) {
    return (
      <div>
        <PageHeader title="User" />
        <div className="ad-card p-6 text-text3">Loading…</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={data.displayName}
        subtitle={data.email}
        actions={
          <Link to="/users" className="ad-btn-secondary">
            <ArrowLeft size={14} /> Back
          </Link>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3 mb-6">
        <Card label="Trips" value={data.counts.trips} />
        <Card label="Feed posts" value={data.counts.feedPosts} />
        <Card label="AI conversations" value={data.counts.aiConversations} />
      </div>

      <div className="ad-card p-6 mb-4">
        <h3 className="font-semibold mb-3">Identity</h3>
        <Row k="ID" v={<span className="font-mono text-xs">{data.id}</span>} />
        <Row k="Email" v={data.email} />
        <Row k="Username" v={data.username ?? '—'} />
        <Row k="Language" v={data.preferredLanguage} />
        <Row k="Telegram ID" v={data.telegramId ?? '—'} />
        <Row k="Last seen" v={fmtRelative(data.lastSeenAt)} />
        <Row k="Created" v={new Date(data.createdAt).toLocaleString()} />
      </div>

      <div className="ad-card p-6 mb-4">
        <h3 className="font-semibold mb-3">Role</h3>
        <div className="flex items-center gap-2">
          {(['user', 'admin', 'super_admin'] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRole.mutate(r)}
              disabled={setRole.isPending || data.role === r}
              className={cn(
                'ad-btn-sm',
                data.role === r
                  ? 'bg-accent/20 text-accent border border-accent/40'
                  : 'ad-btn-secondary',
              )}
            >
              {r}
            </button>
          ))}
        </div>
        {setRole.error && (
          <div className="mt-3 text-error text-sm">
            {(setRole.error as Error).message}
          </div>
        )}
      </div>

      <div className="ad-card p-6 border-error/30">
        <h3 className="font-semibold mb-3 text-error">Danger zone</h3>
        {!confirming ? (
          <button onClick={() => setConfirming(true)} className="ad-btn-danger">
            <Trash2 size={14} /> Delete user
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-text2 text-sm">Permanent. Refresh tokens cascade.</span>
            <button onClick={() => setConfirming(false)} className="ad-btn-secondary ad-btn-sm">
              Cancel
            </button>
            <button
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="ad-btn-danger ad-btn-sm"
            >
              {del.isPending ? 'Deleting…' : 'Yes, delete'}
            </button>
          </div>
        )}
        {del.error && <div className="mt-3 text-error text-sm">{(del.error as Error).message}</div>}
      </div>
    </div>
  );
}

function Card({ label, value }: { label: string; value: number }) {
  return (
    <div className="ad-stat">
      <div className="ad-stat-label">{label}</div>
      <div className="ad-stat-value">{value.toLocaleString()}</div>
    </div>
  );
}
function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex border-b border-border last:border-b-0 py-2 text-sm">
      <div className="w-40 text-text3">{k}</div>
      <div>{v}</div>
    </div>
  );
}
